import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import {
  clearPendingPurchase,
  listPendingPurchases,
  trackPurchase,
} from "@/lib/analytics";

/**
 * Native purchase reconciliation.
 *
 * On the web the payment-return page fires the GA4 `purchase` event because it
 * runs in the same browser the checkout happened in. On native it can't: the
 * PayFast flow and its return URL open in the *system browser*, so the app
 * never renders the return page and the `purchase` event never reaches
 * Firebase — which is why App analytics showed `begin_checkout` but no
 * `purchase`.
 *
 * Instead, every native checkout is recorded in a durable pending registry
 * (see markPendingPurchase). When the member comes back to the app — at launch
 * and on every resume from background — we poll the real payment status (the
 * same RPC the return page uses, set by PayFast's ITN webhook) and fire the
 * `purchase` event once, in-app, so it lands in Firebase.
 *
 * The member needs the same catch-up, not just Firebase. Their booking is made
 * either way, but the confirmation for it — the summary, the visit notes, and
 * for a private room the prompt to send guests their waiver links — renders on
 * the return page, which opened in Safari. They come back to an app still
 * sitting on the pay step, with no sign anything happened. So a completed
 * purchase also announces itself: PURCHASE_CONFIRMED_EVENT carries the payment
 * id, and the app routes to its own /payment/return for it (see the listener in
 * ZuzuAppRuntime), which is the screen that already knows how to say all of
 * that.
 */

/** Fired in-app when a checkout that completed off-app is first noticed. */
export const PURCHASE_CONFIRMED_EVENT = "zz-purchase-confirmed";

async function reconcilePendingPurchases(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const pending = listPendingPurchases();
  for (const entry of pending) {
    try {
      const { data, error } = await supabase.rpc("get_payment_status", {
        p_m_payment_id: entry.transactionId,
      });
      if (error) continue; // transient — try again on the next resume
      const status = (data as { status?: string } | null)?.status;

      if (status === "completed") {
        trackPurchase({
          transactionId: entry.transactionId,
          value: entry.value,
          itemName: entry.itemName,
        });
        // Cleared first: the registry is what stops this firing twice, and a
        // listener that navigates must not be able to re-trigger it.
        clearPendingPurchase(entry.transactionId);
        window.dispatchEvent(
          new CustomEvent(PURCHASE_CONFIRMED_EVENT, {
            detail: { mPaymentId: entry.transactionId },
          }),
        );
      } else if (status === "failed" || status === "cancelled") {
        clearPendingPurchase(entry.transactionId);
      }
      // still 'pending' (or webhook not in yet) → leave it for the next pass;
      // listPendingPurchases ages entries out after 24h.
    } catch {
      // Network hiccup / RPC unavailable — leave the entry for next time.
    }
  }
}

let started = false;

/**
 * Start reconciling native purchases. Safe to call once at app start; no-ops
 * on web and when called more than once.
 */
export function startNativePurchaseReconciler(): void {
  if (started || !Capacitor.isNativePlatform()) return;
  started = true;

  // Confirm anything left over from a previous run (app was killed while
  // PayFast was open).
  void reconcilePendingPurchases();

  // And every time the member returns from the system browser / background.
  import("@capacitor/app")
    .then(({ App }) => {
      App.addListener("resume", () => {
        void reconcilePendingPurchases();
      });
    })
    .catch(() => {
      // @capacitor/app unavailable — launch-time reconciliation still ran.
    });
}
